import { useCallback, useRef, useState, useEffect } from 'react';
import dynamic from 'next/dynamic';
import { GoogleReCaptchaProvider, useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import { createStore } from '@xstate/store';
import { useSelector } from '@xstate/store/react';
import z from 'zod/v4';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Form, Radio } from 'react-aria-components';

import { RadioGroup } from 'components/ui/radio-group';
import { checkAvailability, bookAppointment } from 'actions/shops';
import { getStoresByCountry } from 'actions/shops';
import { useLocale } from 'hooks';
import { useForm } from 'hooks/use-form';
import { usePhonePrefix } from 'hooks/use-phone-prefix';
import { useI18n } from 'context/i18n';

import { Button } from 'components/ui/button';
import { Select, SelectItem } from 'components/ui/select';
import { Checkbox } from 'components/ui/checkbox';
import { Dialog, DialogHeader, DialogRoot } from 'components/ui/dialog';
import { TextField } from 'components/ui/text-field';
import { InStoreSVG } from 'components/iconsSVG/inStore';
import { NotifySVG } from 'components/iconsSVG/notify';
import { PrivacyPolicyText, ShoeCareTermsAndConditionsText } from 'components/legalTexts';
import { ProgressCircle } from 'components/ui/progress-circle';

import { emailSchema, requiredStringSchema, privacySchema } from 'utils/validation';
import { dataLayerHandleEvent } from 'utils/dataLayers';
import { handleReCaptcha } from 'utils/recaptcha';
import { getDomainValue } from 'utils/domain';
import { queryKeys } from 'utils/constants/reactQuery';

import styles from './style.module.css';
const ThankYouMessage = dynamic(() => import('components/thankYouMessage').then((mod) => mod.ThankYouMessage));

const xStore = createStore({
  context: {
    loaded: false,
    name: '',
    email: '',
    telephone: '',
    isOpen: false,
    terms: false,
    privacy: false,
    storeId: null as TODO,
    countryData: {
      stores: [] as TODO[],
      cities: [] as string[],
    },
  },
  on: {
    open: (context) => {
      context.isOpen = true;
    },
    close: (context) => {
      context.isOpen = false;
    },
    change_store: (context, event: { storeId: string }) => {
      context.storeId = event.storeId;
    },
    set_country_data: (context, event: { countryData: { stores: any[]; cities: any[] } }) => {
      context.countryData = event.countryData;
      context.loaded = true;
    },
    change_name: (context, event: { name: string }) => {
      context.name = event.name;
    },
    change_email: (context, event: { email: string }) => {
      context.email = event.email;
    },
    change_telephone: (context, event: { telephone: string }) => {
      context.telephone = event.telephone;
    },
    change_terms: (context, event: { terms: boolean }) => {
      context.terms = event.terms;
    },
    change_privacy: (context, event: { privacy: boolean }) => {
      context.privacy = event.privacy;
    },
  },
});

const appointmentSchema = z.object({
  name: requiredStringSchema,
  email: emailSchema,
  telephone: requiredStringSchema,
  privacy: privacySchema,
  prefix: z.string().optional(),
  terms: privacySchema,
  day: requiredStringSchema,
});

type AppointmentFormValues = z.infer<typeof appointmentSchema>;

function ShoeCareForm() {
  const { t, locale } = useI18n();
  const { country } = useLocale();
  const { selectPrefixes, extractPrefix } = usePhonePrefix();
  const { executeRecaptcha } = useGoogleReCaptcha();
  const handleReCaptchaVerify = useCallback(async () => handleReCaptcha(executeRecaptcha), [executeRecaptcha]);

  const [isSubmitLoading, setIsSubmitLoading] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [submitted, setSubmitted] = useState(false);
  const [filteredCity, setFilteredCity] = useState(null);
  const [filteredStoreId, setFilteredStoreId] = useState(null);
  const [memberDays, setMemberDays] = useState(null);
  const [membersDaysError, setMembersDaysError] = useState(false);
  const [selectedDay, setSelectedDay] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const currentStore = useSelector(xStore, (state) => state.context.storeId);
  const countryData = useSelector(xStore, (state) => state.context.countryData);

  const formRef = useRef(null);

  useEffect(() => {
    if (currentStore) {
      const loadMemberDays = async () => {
        setIsLoading(true);
        try {
          const initialDays = await checkAvailability({ profile: locale, store: currentStore.id });
          if (initialDays) {
            setMemberDays(initialDays as TODO);
          } else {
            setMemberDays({} as TODO);
          }
        } catch (error) {
          setMemberDays({} as TODO);
        } finally {
          setIsLoading(false);
        }
      };

      loadMemberDays();
    }
  }, [currentStore, locale]);

  const appointMutation = useMutation({
    mutationFn: async (values: AppointmentFormValues) => {
      setIsSubmitLoading(true);
      setSubmitError(null);

      try {
        const token = await handleReCaptchaVerify();
        const { name, email, telephone, prefix, day } = values;
        let canBook = false;

        if (token && name && telephone && email && currentStore && day) {
          canBook = true;
        }

        if (!canBook) {
          throw new Error('Error verifying.');
        } else {
          const phonePrefix = extractPrefix({ countryId: prefix ?? '' }) ?? extractPrefix({ countryId: country }) ?? '';

          const appointmentResult = await bookAppointment(locale, {
            name,
            email,
            phonePrefix,
            telephone,
            storeCode: currentStore.id,
            captchaToken: token,
            memberDaysAppointmentDate: day,
          });

          dataLayerHandleEvent({
            event: 'try_personal_shopper',
            event_category: 'personal shopper',
            event_detail_1: currentStore || '',
            event_detail_2: currentStore?.name?.toLowerCase() || '',
          });

          if (appointmentResult) {
            setSubmitted(true);

            return {
              response: appointmentResult,
            };
          } else {
            throw new Error('Error booking appointment');
          }
        }
      } catch (error) {
        setSubmitError(t('tiendas.locator', 'error.api.shoe.care', 'Se ha producido un error') as TODO);

        if (selectedDay === null) {
          setMembersDaysError(true);
        }
      } finally {
        setIsSubmitLoading(false);
      }
    },
  });

  const handleSelectCity = (filterCity: TODO) => {
    setFilteredCity(filterCity as TODO);
    setFilteredStoreId(null);
    xStore.send({ type: 'change_store', storeId: null as TODO });
    setMemberDays(null);
    setSelectedDay(null);
  };

  const handleSelectStore = (filterStore: TODO) => {
    const selectedStore = countryData.stores.filter((storeItem) => storeItem.id === filterStore)[0];
    setFilteredStoreId(filterStore);
    xStore.send({ type: 'change_store', storeId: selectedStore as TODO });
    setSelectedDay(null);
  };

  const { validate, handleSubmit } = useForm({ schema: appointmentSchema });

  const storesData =
    filteredCity ?
      countryData?.stores?.filter(
        (storeItem) =>
          storeItem.cityName === filteredCity && storeItem.bookingAvailability === true && (storeItem.inStoreAppointment || storeItem.videoCallAppointment),
      )
    : countryData?.stores || [];

  let memberDaysComponent = null;

  if (isLoading) {
    memberDaysComponent = <ProgressCircle className="size-4" />;
  } else if (memberDays === null || Object.keys(memberDays).length === 0) {
    memberDaysComponent = (
      <p className="text-neutral-inverted mb-0 text-center text-sm">
        {memberDays === null ? t('tiendas.locator', 'select.store', 'Select a store') : t('tiendas.locator', 'book.no.days', 'No days available')}
      </p>
    );
  } else {
    memberDaysComponent = (
      <div className="">
        <RadioGroup
          label=""
          hiddenLabel
          isRequired
          name="day"
          customRender
          validate={(value) => validate('day', value)}
          onChange={(value) => setSelectedDay(value)}
        >
          <div className={`grid max-w-full grid-cols-3 flex-wrap gap-2 sm:flex sm:flex-row sm:flex-nowrap`}>
            {Object.keys(memberDays).map((dateKey) => {
              const dateObj = new Date(dateKey).toLocaleDateString(undefined, { month: '2-digit', day: '2-digit' });

              return (
                <Radio
                  key={dateKey}
                  value={dateKey}
                  className={`flex h-12 w-12 cursor-pointer items-center justify-center border border-dark p-2 ${selectedDay === dateKey ? 'bg-neutral-inverted text-inverted' : ''}`}
                >
                  {dateObj}
                </Radio>
              );
            })}
          </div>
        </RadioGroup>

        {membersDaysError && <p className="text-error">{t('generico', 'select', 'Se ha producido un error')}</p>}
      </div>
    );
  }

  return (
    <>
      {submitted ?
        <ThankYouMessage className="flex flex-col items-center" type="appointment" />
      : <>
          <div className="flex flex-col items-center gap-2.5">
            <NotifySVG />
            <p className="mb-2 font-medium">{t('ficha.producto', 'modal.members.only.title', 'Members Only')}</p>
            <p
              className="m-0 text-[length:var(--title-size)]"
              dangerouslySetInnerHTML={{ __html: t('tiendas.locator', 'banner.title', 'Book your appointment') }}
            />
            <p className="m-0">{currentStore?.name || ''}</p>
            <p
              className="m-0 text-[length:var(--description-size)] text-[var(--color-primary)]"
              dangerouslySetInnerHTML={{
                __html: t('tiendas.locator', 'book.subtitle', '***Make an appointment with a member of our team for a personalized shopping experience.'),
              }}
            />
          </div>

          <Form
            className="flex w-full flex-col items-center gap-2"
            onSubmit={(e) => {
              appointMutation.mutate(handleSubmit(e));
            }}
            ref={formRef}
          >
            <div className="mt-1 flex flex-col gap-2.5">
              <div className="flex w-full flex-col gap-2 sm:flex-row sm:justify-between sm:gap-0">
                {countryData && countryData.cities ?
                  <Select
                    isRequired
                    name="city"
                    label={t('tiendas.locator', 'select.city', 'Select a city')}
                    variant={getDomainValue({ camper: 'box', nnormal: 'box', camperlab: 'default' })}
                    items={countryData?.cities?.map((city) => ({ label: city, value: city })) ?? []}
                    selectedKey={filteredCity}
                    onSelectionChange={(key) => {
                      handleSelectCity(key as string);
                    }}
                    className="w-full sm:w-[50%]"
                  >
                    {(item) => (
                      <SelectItem className="flex max-w-[--trigger-width]" id={item.value}>
                        {item.label}
                      </SelectItem>
                    )}
                  </Select>
                : null}

                {countryData && countryData.stores ?
                  <Select
                    isRequired
                    name="store"
                    label={
                      storesData?.length > 1 ?
                        t('tiendas.locator', 'select.store', 'Select a store')
                      : t('tiendas.locator', 'no.stores.for.city', 'No stores available for this city')
                    }
                    isDisabled={storesData?.length === 0}
                    variant={getDomainValue({ camper: 'box', nnormal: 'box', camperlab: 'default' })}
                    items={storesData?.map((store) => ({ label: store.name, value: store.id })) ?? []}
                    selectedKey={filteredStoreId}
                    onSelectionChange={(key) => {
                      handleSelectStore(key as string);
                    }}
                    className="w-full sm:w-[50%] sm:max-w-[50%]"
                  >
                    {(item) => (
                      <SelectItem className="flex max-w-[--trigger-width]" id={item.value}>
                        {item.label}
                      </SelectItem>
                    )}
                  </Select>
                : null}
              </div>

              <div className="my-2 flex w-full flex-row items-center justify-center gap-2 sm:flex-col">
                <div className="flex gap-1.5 bg-neutral-inverted p-2 text-inverted">
                  <InStoreSVG className="stroke" />
                  <span className="text-center">In-Store</span>
                </div>

                {memberDaysComponent}
              </div>

              <div className="flex w-full flex-col md:flex-row">
                <div className="basis-full">
                  <TextField
                    variant="box"
                    validate={(value) => validate('name', value)}
                    label="Name"
                    type="text"
                    name="name"
                    placeholder="Name"
                    onChange={(value) => {
                      xStore.send({ type: 'change_name', name: value });
                    }}
                  />
                </div>
              </div>
              <div className="flex w-full flex-col gap-2 md:flex-row">
                <div className="basis-full md:basis-6/12 md:lab:basis-full">
                  <TextField
                    variant="box"
                    label="Email"
                    type="email"
                    name="email"
                    validate={(value) => validate('email', value)}
                    placeholder="Email"
                    onChange={(value) => {
                      xStore.send({ type: 'change_email', email: value });
                    }}
                  />
                </div>
                <div className="basis-full md:basis-6/12 md:lab:basis-full">
                  <div className="flex lab:flex-col lab:gap-2">
                    <Select
                      label={t('generico', 'prefijo.telefono', 'Telephone')}
                      name="prefix"
                      items={selectPrefixes}
                      defaultSelectedKey={country}
                      variant={getDomainValue({ camper: 'box', nnormal: 'box', camperlab: 'default' })}
                      className="w-[45%] min-w-0"
                    />
                    <TextField
                      name="telephone"
                      label={t('generico', 'telefono', 'Telephone')}
                      validate={(value) => validate('telephone', value)}
                      type="tel"
                      variant="box"
                      className="w-[55%]"
                    />
                  </div>
                </div>
              </div>
              <Checkbox
                onChange={(value) => {
                  xStore.send({ type: 'change_terms', terms: value });
                }}
                name="terms"
                isRequired
                boxClassName="size-5"
                className="items-center"
              >
                <ShoeCareTermsAndConditionsText className="text-wrap text-xs lab:max-lg:text-sm" />
              </Checkbox>
              <Checkbox
                onChange={(value) => {
                  xStore.send({ type: 'change_privacy', privacy: value });
                }}
                name="privacy"
                isRequired
                boxClassName="size-5"
                className="items-center"
              >
                <PrivacyPolicyText className="text-wrap text-xs lab:max-lg:text-sm" />
              </Checkbox>

              <div className="flex w-full justify-center">
                <Button type="submit" variant="solid" className={styles.submitButton}>
                  {isSubmitLoading ?
                    <ProgressCircle className="size-4" />
                  : t('generico', 'send', 'Send')}
                </Button>
              </div>
              {submitError && <p className="text-error">{submitError}</p>}
            </div>
          </Form>
        </>
      }
    </>
  );
}

type CountryData = {
  stores: any[];
  cities: any[];
};

export function ShoeCareAppointment() {
  const { country, language } = useLocale();
  const { locale } = useI18n();

  const isOpen = useSelector(xStore, (state) => state.context.isOpen);
  const loaded = useSelector(xStore, (state) => state.context.loaded);

  const { data, status } = useQuery<CountryData, Error>({
    queryKey: [queryKeys.stores.all, locale],
    queryFn: async () => {
      console.log('queryFn');
      const countryData = await getStoresByCountry(null, locale, country, true, true);
      // Ensure the result is an array of Store
      if (!countryData) {
        throw new Error('Invalid country data');
      } else {
        xStore.send({ type: 'set_country_data', countryData });
      }
      return countryData;
    },
    enabled: isOpen,
  });

  useEffect(() => {
    const openFn = () => {
      xStore.send({ type: 'open' });
    };

    window.showShoeCareAppointmentModal = openFn;
  }, [language, country]);

  const handleClose = () => {
    xStore.send({ type: 'close' });
  };

  useEffect(() => {
    if (isOpen) {
      document.documentElement.style['scrollbarGutter'] = 'auto';
    }
  }, [isOpen]);

  useEffect(() => {
    return () => {
      xStore.send({ type: 'close' });
    };
  }, []);

  return (
    <DialogRoot className="!m-0 h-full w-full sm:h-auto sm:w-[660px]" isOpen={isOpen} onOpenChange={handleClose} showOverlay>
      <Dialog className="px-[24px] py-[20px]">
        <div className="flex flex-col gap-2.5">
          <DialogHeader label="Event Modal" hidden />
          <GoogleReCaptchaProvider
            reCaptchaKey={process.env.RECAPTCHA_KEY ?? ''}
            language={locale}
            useRecaptchaNet
            scriptProps={{
              async: false,
              defer: false,
              appendTo: 'body',
            }}
          >
            {loaded ?
              <ShoeCareForm />
            : <div className="flex h-full w-full items-center justify-center">
                <ProgressCircle className="size-4" />
              </div>
            }
          </GoogleReCaptchaProvider>
        </div>
      </Dialog>
    </DialogRoot>
  );
}
